/** @jsx jsx */
import React from 'react';
import {css, jsx, SerializedStyles} from '@emotion/react';
import {EzHeading} from '@ezcater/recipe';
import styled from '@emotion/styled';
import {breakpoints} from '@src/styles';

const circleHeight = '48px';
const iconHeight = '24px';
const IconContainer = styled('span')`
  background-color: ${({theme}) => theme.colors.grays[200]};
  border: 0;
  border-radius: 50%;
  color: ${({theme}) => theme.colors.brandColors.ezBlue};
  line-height: 0;
  margin-right: ${({theme}) => theme.recipeSpacing.md};
  outline: none;
  padding: calc((${circleHeight} - ${iconHeight}) / 2);
`;

const ContactText = styled('div')`
  display: flex;
  flex-direction: column;
`;
const StyledSubText = styled('span')`
  font-weight: ${({theme}) => theme.fontWeights.normal};
`;
const StyledEzHeading = styled(EzHeading)`
  color: ${({theme}) => theme.colors.grays[700]};
`;
const contactRowStyle = css`
  align-items: center;
  display: flex;
`;
const mobileHiddenContactRow = css`
  ${breakpoints.mediumAndDown(`display: none;`)};
`;
const largeHiddenContactRow = css`
  ${breakpoints.mediumAndUp(`display: none;`)};
`;

const anchorCss = css`
  cursor: pointer;
`;

interface ContactRowProps {
  styles?: SerializedStyles;
  heading: string;
  hideDesktop?: boolean;
  hideMobile?: boolean;
  href?: string;
  Icon: React.ComponentType<{fontSize: string}>;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  RowElement: 'a' | 'div';
  subText: string;
  target?: string;
}

const ContactRow: React.FC<ContactRowProps> = ({
  styles,
  heading,
  hideDesktop,
  hideMobile,
  href,
  Icon,
  onClick,
  RowElement,
  subText,
  target,
}) => {
  const rowCSS = [contactRowStyle];
  if (hideMobile) rowCSS.push(mobileHiddenContactRow);
  if (hideDesktop) rowCSS.push(largeHiddenContactRow);
  if (styles) rowCSS.push(styles);

  return (
    <RowElement css={rowCSS} onClick={onClick} href={href} target={target}>
      <IconContainer>
        <Icon fontSize={iconHeight} />
      </IconContainer>
      <ContactText>
        <StyledEzHeading size="5">{heading}</StyledEzHeading>
        <StyledSubText>{subText}</StyledSubText>
      </ContactText>
    </RowElement>
  );
};

export const ClickableContactRow: React.FC<Omit<ContactRowProps, 'RowElement'>> = props => (
  <ContactRow RowElement="a" styles={anchorCss} {...props} />
);

export const StaticContactRow: React.FC<Omit<ContactRowProps, 'RowElement'>> = props => (
  <ContactRow RowElement="div" {...props} />
);
